// services/google-sheets.ts — Platik-IA
// Cliente REST para Google Sheets API v4.
// Usa el provider_token de la sesión de Supabase (OAuth scope: spreadsheets).

import { uuid, isoToSheetDate, sheetDateToIso } from '../shared/utils/format';

const BASE_URL = 'https://sheets.googleapis.com/v4/spreadsheets';

export const SHEET_NAMES = {
  TRANSACTIONS: 'Transacciones',
  CATEGORIES:   'Categorías',
} as const;

export const TX_HEADERS = ['Fecha', 'Tipo', 'Monto', 'Descripción', 'Categoría', 'Subcategoría', 'ID'];

// ── Tipos ────────────────────────────────────────────────────────────────

export interface SheetTransaction {
  id:           string;
  fecha:        string;
  tipo:         'Gasto' | 'Ingreso';
  monto:        number;
  descripcion:  string;
  categoria:    string;
  subcategoria: string;
}

// ── Helper de fetch autenticado ───────────────────────────────────────────

async function sheetsRequest<T>(
  path:   string,
  token:  string,
  opts:   RequestInit = {}
): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...opts,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type':  'application/json',
      ...opts.headers,
    },
  });

  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Sheets API ${res.status}: ${err.slice(0, 200)}`);
  }

  return res.json() as Promise<T>;
}

// ── Crear un nuevo spreadsheet ────────────────────────────────────────────

export async function createSpreadsheet(
  token: string,
  title: string,
  cats:  Record<string, string[]> = {}
): Promise<string> {
  // Encabezado de categorías + filas sembradas (compatibilidad con v2)
  const catHeader = {
    values: [
      { userEnteredValue: { stringValue: 'Categoría' }, userEnteredFormat: { textFormat: { bold: true } } },
      { userEnteredValue: { stringValue: 'Subcategoría' }, userEnteredFormat: { textFormat: { bold: true } } },
    ],
  };
  const catRows = Object.entries(cats).flatMap(([cat, subs]) =>
    (subs.length ? subs : ['']).map(sub => ({
      values: [
        { userEnteredValue: { stringValue: cat } },
        { userEnteredValue: { stringValue: sub } },
      ],
    }))
  );

  const body = {
    properties: { title },
    sheets: [
      {
        properties: { title: SHEET_NAMES.TRANSACTIONS },
        data: [{
          rowData: [{
            values: TX_HEADERS.map(h => ({
              userEnteredValue: { stringValue: h },
              userEnteredFormat: { textFormat: { bold: true } },
            })),
          }],
        }],
      },
      {
        properties: { title: SHEET_NAMES.CATEGORIES },
        data: [{ rowData: [catHeader, ...catRows] }],
      },
    ],
  };

  const res = await sheetsRequest<{ spreadsheetId: string }>('', token, {
    method: 'POST',
    body:   JSON.stringify(body),
  });

  return res.spreadsheetId;
}

// ── Agregar una transacción (append) ──────────────────────────────────────

export async function appendTransaction(
  token:         string,
  spreadsheetId: string,
  tx:            SheetTransaction
): Promise<void> {
  const values = [[
    isoToSheetDate(tx.fecha), tx.tipo, tx.monto, tx.descripcion,
    tx.categoria, tx.subcategoria, tx.id,
  ]];

  await sheetsRequest(
    `/${spreadsheetId}/values/${SHEET_NAMES.TRANSACTIONS}!A:G:append?valueInputOption=USER_ENTERED`,
    token,
    { method: 'POST', body: JSON.stringify({ values }) }
  );
}

// ── Actualizar una transacción (buscar por ID en col G) ───────────────────

export async function updateTransactionById(
  token:         string,
  spreadsheetId: string,
  tx:            SheetTransaction
): Promise<void> {
  // Buscar la fila por ID
  const rowIndex = await findRowById(token, spreadsheetId, tx.id);
  if (rowIndex === -1) {
    // Si no existe, crear (puede pasar en primera sync)
    await appendTransaction(token, spreadsheetId, tx);
    return;
  }

  const range = `${SHEET_NAMES.TRANSACTIONS}!A${rowIndex}:G${rowIndex}`;
  const values = [[isoToSheetDate(tx.fecha), tx.tipo, tx.monto, tx.descripcion, tx.categoria, tx.subcategoria, tx.id]];

  await sheetsRequest(
    `/${spreadsheetId}/values/${encodeURIComponent(range)}?valueInputOption=USER_ENTERED`,
    token,
    { method: 'PUT', body: JSON.stringify({ values }) }
  );
}

// ── Eliminar una transacción (limpiar la fila) ────────────────────────────

export async function deleteTransactionById(
  token:         string,
  spreadsheetId: string,
  id:            string
): Promise<void> {
  const rowIndex = await findRowById(token, spreadsheetId, id);
  if (rowIndex === -1) return;

  // Usamos batchUpdate para eliminar la fila (no solo limpiarla)
  const sheetId = await getSheetId(token, spreadsheetId, SHEET_NAMES.TRANSACTIONS);
  await sheetsRequest(
    `/${spreadsheetId}:batchUpdate`,
    token,
    {
      method: 'POST',
      body: JSON.stringify({
        requests: [{
          deleteDimension: {
            range: { sheetId, dimension: 'ROWS', startIndex: rowIndex - 1, endIndex: rowIndex },
          },
        }],
      }),
    }
  );
}

// ── Leer todas las transacciones (para sync inicial) ──────────────────────

export async function readAllTransactions(
  token:         string,
  spreadsheetId: string
): Promise<SheetTransaction[]> {
  const res = await sheetsRequest<{ values?: string[][] }>(
    `/${spreadsheetId}/values/${SHEET_NAMES.TRANSACTIONS}!A2:G`,
    token
  );

  return (res.values ?? [])
    .filter(row => row[0] || row[2])          // descartar filas vacías (sin fecha ni monto)
    .map(row => ({
      fecha:        sheetDateToIso(row[0] ?? ''),
      tipo:         (row[1] as 'Gasto' | 'Ingreso') ?? 'Gasto',
      monto:        parseFloat(row[2]) || 0,
      descripcion:  row[3] ?? '',
      categoria:    row[4] ?? '',
      subcategoria: row[5] ?? '',
      id:           row[6] || uuid(),         // v2 no tiene columna ID → generar UUID
    }));
}

// ── Backfill de IDs (compatibilidad con planillas v2 sin columna ID) ──────

/** Escribe un UUID en la columna ID (G) de cada fila que no lo tenga.
 *  Idempotente: conserva los IDs existentes. Deja una planilla v2 en formato v3
 *  para que el sync por UUID (editar/borrar) y las re-importaciones no dupliquen. */
export async function backfillSheetIds(token: string, spreadsheetId: string): Promise<void> {
  const res = await sheetsRequest<{ values?: string[][] }>(
    `/${spreadsheetId}/values/${SHEET_NAMES.TRANSACTIONS}!A2:G`,
    token
  );
  const rows = res.values ?? [];
  if (rows.length === 0) return;

  let changed = false;
  const idColumn = rows.map(row => {
    if (!row[0] && !row[2]) return [''];   // fila vacía → sin ID
    if (row[6]) return [row[6]];           // ya tiene ID
    changed = true;
    return [uuid()];
  });

  if (!changed) return;

  await sheetsRequest(
    `/${spreadsheetId}/values/${SHEET_NAMES.TRANSACTIONS}!G2:G${rows.length + 1}?valueInputOption=RAW`,
    token,
    { method: 'PUT', body: JSON.stringify({ values: idColumn }) }
  );
}

// ── Guardar categorías en la hoja ─────────────────────────────────────────

export async function writeCategories(
  token:         string,
  spreadsheetId: string,
  cats:          Record<string, string[]>
): Promise<void> {
  const values: string[][] = [['Categoría', 'Subcategoría']];
  for (const [cat, subs] of Object.entries(cats)) {
    if (subs.length === 0) { values.push([cat, '']); continue; }
    for (const sub of subs) values.push([cat, sub]);
  }

  // Limpiar y reescribir
  await sheetsRequest(
    `/${spreadsheetId}/values/${SHEET_NAMES.CATEGORIES}!A:B:clear`,
    token,
    { method: 'POST', body: '{}' }
  );

  await sheetsRequest(
    `/${spreadsheetId}/values/${SHEET_NAMES.CATEGORIES}!A1:B?valueInputOption=USER_ENTERED`,
    token,
    { method: 'PUT', body: JSON.stringify({ values }) }
  );
}

// ── Helpers internos ──────────────────────────────────────────────────────

async function findRowById(token: string, spreadsheetId: string, id: string): Promise<number> {
  const res = await sheetsRequest<{ values?: string[][] }>(
    `/${spreadsheetId}/values/${SHEET_NAMES.TRANSACTIONS}!G:G`,
    token
  );
  const rows = res.values ?? [];
  const idx  = rows.findIndex(r => r[0] === id);
  return idx === -1 ? -1 : idx + 1; // +1 porque Sheets es 1-indexed
}

async function getSheetId(token: string, spreadsheetId: string, sheetName: string): Promise<number> {
  const res = await sheetsRequest<{ sheets: { properties: { title: string; sheetId: number } }[] }>(
    `/${spreadsheetId}?fields=sheets.properties`,
    token
  );
  const sheet = res.sheets.find(s => s.properties.title === sheetName);
  return sheet?.properties.sheetId ?? 0;
}
